import { create } from 'zustand'
import { Room } from 'colyseus.js'
import { networkClient } from '../network/client'

export type Screen = 'menu' | 'lobby' | 'waiting' | 'game'
export type PlayerRole = 'flux' | 'logic' | 'spark' | 'echo'

export interface Player {
  sessionId: string
  nickname: string
  role: PlayerRole
  x: number
  y: number
  score: number
  isReady: boolean
  isAI: boolean
}

export interface RoomInfo {
  roomId: string
  name: string
  players: number
  maxPlayers: number
}

export interface ActivePuzzle {
  puzzleId: string
  type: string
  difficulty: string
  // Pattern puzzle data
  sequence?: string[]
  // Sequence puzzle data
  title?: string
  description?: string
  blocks?: string[]
  // Logic puzzle data
  inputs?: string[]
  expression?: string
  // Debug puzzle data
  code?: string[]
}

export interface GameOverData {
  reason: 'time' | 'stability' | 'victory'
  timePlayed: number
  puzzlesSolved: number
  totalScore: number
  multiplier: number
  finalScore: number
  players: number
}

export interface AIAtMarker {
  aiId: string
  markerId: string
  role: PlayerRole
}

export interface AIContextMenu {
  type: 'map' | 'marker' | 'ai'
  screenX: number
  screenY: number
  worldX?: number
  worldY?: number
  markerId?: string
  markerType?: string
  markerDifficulty?: string
  aiId?: string
  aiList: { id: string; name: string; role: string }[]
}

interface GameState {
  // UI State
  screen: Screen
  nickname: string
  selectedRole: PlayerRole
  error: string | null

  // Room state
  currentRoomId: string | null
  isHost: boolean
  players: Record<string, Player>
  mySessionId: string | null

  // Game state
  phase: 'lobby' | 'explore' | 'puzzle' | 'cutscene'
  timer: number
  stability: number
  squad: 'solo' | 'duo' | 'trio' | 'full'

  // Available rooms
  availableRooms: RoomInfo[]

  // Puzzle state
  activePuzzle: ActivePuzzle | null

  // Game over state
  gameOverData: GameOverData | null

  // AI at marker tracking
  aiAtMarkers: AIAtMarker[]

  // AI context menu
  aiContextMenu: AIContextMenu | null

  // Actions
  setScreen: (screen: Screen) => void
  setNickname: (nickname: string) => void
  setSelectedRole: (role: PlayerRole) => void
  setError: (error: string | null) => void

  // Network actions
  joinLobby: () => Promise<void>
  createRoom: (roomName: string) => Promise<void>
  joinRoom: (roomId: string) => Promise<void>
  quickJoin: () => Promise<void>
  leaveRoom: () => void
  toggleReady: () => void
  startGame: () => void
  changeRole: (role: PlayerRole) => void
  refreshRooms: () => Promise<void>

  // Puzzle actions
  requestPuzzle: (difficulty?: string, type?: string) => void
  closePuzzle: () => void

  // AI context menu actions
  showAIContextMenu: (menu: AIContextMenu) => void
  closeAIContextMenu: () => void
  sendAICommand: (command: 'move' | 'investigate' | 'return', aiId: string, data?: any) => void

  // State sync
  syncFromRoom: (room: Room) => void
}

export const useGameStore = create<GameState>((set, get) => ({
  // Initial UI state
  screen: 'menu',
  nickname: '',
  selectedRole: 'flux',
  error: null,

  // Initial room state
  currentRoomId: null,
  isHost: false,
  players: {},
  mySessionId: null,

  // Initial game state
  phase: 'lobby',
  timer: 1800,
  stability: 100,
  squad: 'solo',

  // Available rooms
  availableRooms: [],

  // Puzzle state
  activePuzzle: null,

  // Game over state
  gameOverData: null,

  // AI at marker tracking
  aiAtMarkers: [],

  // AI context menu
  aiContextMenu: null,

  // Basic setters
  setScreen: (screen) => set({ screen }),
  setNickname: (nickname) => set({ nickname }),
  setSelectedRole: (role) => set({ selectedRole: role }),
  setError: (error) => set({ error }),

  // Network actions
  joinLobby: async () => {
    try {
      const { nickname } = get()
      const room = await networkClient.joinLobby(nickname || 'Gracz')

      room.state.rooms.onAdd((roomInfo: any) => {
        const rooms = get().availableRooms
        set({
          availableRooms: [...rooms, {
            roomId: roomInfo.roomId,
            name: roomInfo.name,
            players: roomInfo.players,
            maxPlayers: roomInfo.maxPlayers,
          }]
        })
      })

      room.state.rooms.onRemove((roomInfo: any, _key: string) => {
        const rooms = get().availableRooms.filter(r => r.roomId !== roomInfo.roomId)
        set({ availableRooms: rooms })
      })

      room.state.rooms.onChange((roomInfo: any) => {
        const rooms = get().availableRooms.map(r =>
          r.roomId === roomInfo.roomId
            ? { ...r, players: roomInfo.players, name: roomInfo.name }
            : r
        )
        set({ availableRooms: rooms })
      })

      set({ screen: 'lobby', error: null })
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie polaczyc' })
    }
  },

  createRoom: async (roomName) => {
    try {
      const { nickname, selectedRole } = get()
      await networkClient.createRoom(roomName, nickname, selectedRole)

      const room = networkClient.getGameRoom()
      if (room) {
        get().syncFromRoom(room)
        set({ screen: 'waiting', isHost: true, error: null })
      }
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie stworzyc pokoju' })
    }
  },

  joinRoom: async (roomId) => {
    try {
      const { nickname, selectedRole } = get()
      const room = await networkClient.joinGameRoom(roomId, nickname, selectedRole)
      get().syncFromRoom(room)
      set({ screen: 'waiting', isHost: false, error: null })
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie dolaczyc do pokoju' })
    }
  },

  quickJoin: async () => {
    try {
      const { nickname, selectedRole } = get()
      const room = await networkClient.quickJoin(nickname, selectedRole)
      get().syncFromRoom(room)
      set({ screen: 'waiting', isHost: true, error: null })
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie dolaczyc' })
    }
  },

  leaveRoom: () => {
    networkClient.leaveGame()
    set({
      screen: 'menu',
      currentRoomId: null,
      players: {},
      mySessionId: null,
      isHost: false,
      phase: 'lobby',
      activePuzzle: null,
      gameOverData: null,
      timer: 1800,
      stability: 100,
      availableRooms: [],
      error: null,
      aiAtMarkers: [],
    })
  },

  toggleReady: () => {
    networkClient.toggleReady()
  },

  startGame: () => {
    networkClient.startGame()
  },

  changeRole: (role) => {
    networkClient.changeRole(role)
    set({ selectedRole: role })
  },

  refreshRooms: async () => {
    try {
      const rooms = await networkClient.getAvailableRooms()
      set({
        availableRooms: rooms.map(r => ({
          roomId: r.roomId,
          name: r.metadata?.roomName || `Pokoj ${r.roomId.slice(0, 4)}`,
          players: r.clients,
          maxPlayers: r.maxClients,
        }))
      })
    } catch (error) {
      console.error('Failed to refresh rooms:', error)
    }
  },

  requestPuzzle: (difficulty = 'easy', type = 'pattern') => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_puzzle', { difficulty, type })
    }
  },

  closePuzzle: () => {
    set({ activePuzzle: null })
  },

  // AI context menu actions
  showAIContextMenu: (menu) => {
    set({ aiContextMenu: menu })
  },

  closeAIContextMenu: () => {
    set({ aiContextMenu: null })
  },

  sendAICommand: (command, aiId, data) => {
    const room = networkClient.getGameRoom()
    if (!room) return

    switch (command) {
      case 'move':
        room.send('ai_move', { aiId, x: data.x, y: data.y })
        console.log(`🤖 Wysyłam AI ${aiId} do (${data.x}, ${data.y})`)
        break
      case 'investigate':
        room.send('ai_investigate', { aiId, markerId: data.markerId, x: data.x, y: data.y })
        console.log(`🤖 Wysyłam AI ${aiId} do zbadania ${data.markerId}`)
        break
      case 'return':
        room.send('ai_return', { aiId })
        console.log(`🤖 Wzywam AI ${aiId} z powrotem`)
        break
    }
    set({ aiContextMenu: null })
  },

  syncFromRoom: (room) => {
    set({
      currentRoomId: room.roomId,
      mySessionId: room.sessionId,
    })

    // Helper to convert player data
    const toPlayer = (player: any, sessionId: string): Player => ({
      sessionId,
      nickname: player.nickname || `Gracz${sessionId.slice(0, 4)}`,
      role: player.role as PlayerRole,
      x: player.x,
      y: player.y,
      score: player.score,
      isReady: player.isReady,
      isAI: player.isAI,
    })

    // Helper to refresh all players from room state (unused due to schema sync issues)
    const refreshPlayers = () => {
      const players: Record<string, Player> = {}
      room.state.players.forEach((player: any, sessionId: string) => {
        players[sessionId] = toPlayer(player, sessionId)
      })
      set({ players })
    }

    // Wait for first state change to do initial sync
    room.onStateChange.once(() => {
      refreshPlayers()
    })

    // Listen for player additions (for players joining after us)
    room.state.players.onAdd((player: any, sessionId: string) => {
      const players = { ...get().players }
      players[sessionId] = toPlayer(player, sessionId)
      set({ players })

      // Track individual player changes (ready state, position, etc.)
      player.onChange(() => {
        const currentPlayers = { ...get().players }
        if (currentPlayers[sessionId]) {
          currentPlayers[sessionId] = toPlayer(player, sessionId)
          set({ players: currentPlayers })
        }
      })
    })

    // Listen for player removals
    room.state.players.onRemove((_: any, sessionId: string) => {
      const players = { ...get().players }
      delete players[sessionId]
      set({ players })
    })

    // Sync game state changes
    room.state.onChange(() => {
      set({
        phase: room.state.phase as any,
        timer: room.state.timer,
        stability: room.state.stability,
        squad: room.state.squad as any,
      })
    })

    // Handle messages
    room.onMessage('game_started', () => {
      set({ screen: 'game', phase: 'explore' })

      // Request fresh players sync after a short delay (to get updated positions)
      setTimeout(() => {
        room.send('request_players_sync')
      }, 500)
    })

    room.onMessage('role_assigned', (data: { role: string }) => {
      set({ selectedRole: data.role as PlayerRole })
    })

    room.onMessage('error', (data: { message: string }) => {
      set({ error: data.message })
    })

    // Direct players sync (workaround for schema sync issue)
    room.onMessage('players_sync', (data: { players: any[] }) => {
      const players: Record<string, Player> = {}
      data.players.forEach((p: any) => {
        players[p.sessionId] = {
          sessionId: p.sessionId,
          nickname: p.nickname,
          role: p.role as PlayerRole,
          x: p.x,
          y: p.y,
          score: p.score,
          isReady: p.isReady,
          isAI: p.isAI
        }
      })
      set({ players })
    })

    // Empty handler to suppress warning (GameScene handles this)
    room.onMessage('player_moved', () => {})

    room.onMessage('puzzle_started', (data: any) => {
      set({
        activePuzzle: {
          puzzleId: data.puzzleId,
          type: data.type,
          difficulty: data.difficulty,
          // Pattern puzzle
          sequence: data.sequence,
          // Sequence puzzle
          title: data.title,
          description: data.description,
          blocks: data.blocks,
          // Logic puzzle
          inputs: data.inputs,
          expression: data.expression,
          // Debug puzzle
          code: data.code,
        },
        phase: 'puzzle',
      })
    })

    // puzzle_solved: Don't close puzzle here! Let PuzzlePanel show solved state.
    // User clicks "Kontynuuj" which calls closePuzzle()
    room.onMessage('puzzle_solved', () => {
      set({ phase: 'explore' })
    })

    // Game state sync (timer, stability, phase, squad)
    room.onMessage('game_state_sync', (data: { timer: number; stability: number; phase: string; squad: string }) => {
      set({
        timer: data.timer,
        stability: data.stability,
        phase: data.phase as any,
        squad: data.squad as any,
      })
    })

    // Game over
    room.onMessage('game_over', (data: GameOverData) => {
      set({ gameOverData: data })
    })

    // AI at marker
    room.onMessage('ai_at_marker', (data: { aiId: string; markerId: string; role: string }) => {
      console.log(`📍 AI at marker received:`, data)
      const current = get().aiAtMarkers
      // Check if already tracked
      const exists = current.find(a => a.aiId === data.aiId && a.markerId === data.markerId)
      if (!exists) {
        console.log(`📍 Adding AI ${data.aiId} to markers list`)
        set({
          aiAtMarkers: [...current, {
            aiId: data.aiId,
            markerId: data.markerId,
            role: data.role as PlayerRole,
          }]
        })
      }
    })

    // AI command received (clear ai_at_marker when AI moves away)
    room.onMessage('ai_command_received', (data: { aiId: string; command: string }) => {
      if (data.command === 'move' || data.command === 'return') {
        const current = get().aiAtMarkers
        set({
          aiAtMarkers: current.filter(a => a.aiId !== data.aiId)
        })
      }
    })

    // Drop-in: new player took over an AI
    room.onMessage('player_drop_in', (data: { sessionId: string; nickname: string; role: string; previousAI: string }) => {
      console.log(`🔄 Drop-in: ${data.nickname} przejął AI ${data.previousAI}`)
      // Players will be synced via players_sync
    })

    // Drop-out: player left and became AI
    room.onMessage('player_drop_out', (data: { sessionId: string; nickname: string; role: string; aiId: string; aiNickname: string }) => {
      console.log(`🔄 Drop-out: ${data.nickname} opuścił grę, teraz ${data.aiNickname}`)
      // Players will be synced via players_sync
    })

    // Drop-in success (for the joining player)
    room.onMessage('drop_in_success', (data: { role: string; x: number; y: number; previousAI: string }) => {
      console.log(`✅ Dołączyłeś do gry jako ${data.role.toUpperCase()}, przejmując ${data.previousAI}`)
      set({
        screen: 'game',
        phase: 'explore',
        selectedRole: data.role as PlayerRole,
      })
    })
  },
}))
